import { API_BASE_URL } from "../config";
import {
  PrepareSharedFolderResponse,
  SharedFolderTargetType,
  VerifySharedFolderResponse,
} from "../types";

const apiUrl = `${API_BASE_URL}/api`;
const ADMIN_CONSENT_MESSAGE = "sourcestone-admin-consent-complete";
export const ADMIN_CONSENT_FAILED_MESSAGE = "Admin consent failed. Retry or contact support.";
const ADMIN_CONSENT_DENIED_MESSAGE = "Admin consent not approved.";
export const STORAGE_SETUP_FAILURE_MESSAGE = "Setup failed. Retry or contact Sourcestone support.";
const STORAGE_SIGN_IN_REQUIRED_MESSAGE =
  "Sign-in or consent needed. Complete Approve storage access, sign in again, then retry.";
const STORAGE_UNAVAILABLE_MESSAGE = "Storage setup is temporarily unavailable. Retry shortly.";
const INVALID_STORAGE_LINK_MESSAGE =
  "Invalid link. Use a site URL or folder Copy link and check the selected access level.";

async function storageErrorMessage(
  response: Response,
  preparingLocation: boolean
): Promise<string> {
  if (response.status === 401) return STORAGE_SIGN_IN_REQUIRED_MESSAGE;
  if (response.status === 503) return STORAGE_UNAVAILABLE_MESSAGE;
  if (response.status === 422 && preparingLocation) return INVALID_STORAGE_LINK_MESSAGE;
  if (response.status === 500) return STORAGE_SETUP_FAILURE_MESSAGE;
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === "object" && "detail" in body) {
    const detail = body.detail;
    if (typeof detail === "string" && detail.trim() && detail !== "Error") return detail;
  }
  return STORAGE_SETUP_FAILURE_MESSAGE;
}

async function requestStorage<T>(
  url: string,
  options: RequestInit,
  preparingLocation = false
): Promise<T> {
  const response = await fetch(url, options).catch(() => {
    throw new Error(STORAGE_SETUP_FAILURE_MESSAGE);
  });
  if (!response.ok) throw new Error(await storageErrorMessage(response, preparingLocation));
  return response.json().catch(() => {
    throw new Error(STORAGE_SETUP_FAILURE_MESSAGE);
  });
}

export const beginBackendAdminConsent = async (): Promise<void> => {
  const dialogUrl = `${window.location.origin}${window.location.pathname}?action=admin-consent`;

  await new Promise<void>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 70, width: 45, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(ADMIN_CONSENT_FAILED_MESSAGE));
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;
        const cleanup = () => {
          try {
            dialog.close();
          } catch {
            // Closing an already-closed Office dialog is harmless.
          }
        };
        const finishResolve = () => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve();
        };
        const finishReject = (message: string) => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error(message));
        };
        const onMessage = (args: { message: string; origin: string } | { error: number }) => {
          if ("error" in args) return;
          try {
            const payload = JSON.parse(args.message || "{}");
            if (payload.type !== ADMIN_CONSENT_MESSAGE) return;
            if (payload.success) {
              finishResolve();
              return;
            }
            finishReject(
              payload.error === "access_denied"
                ? ADMIN_CONSENT_DENIED_MESSAGE
                : ADMIN_CONSENT_FAILED_MESSAGE
            );
          } catch {
            finishReject(ADMIN_CONSENT_FAILED_MESSAGE);
          }
        };
        const onDialogEvent = (args: { message: string; origin: string } | { error: number }) => {
          if ("message" in args) return;
          if (args.error === 12006) {
            finishReject("Admin consent cancelled.");
            return;
          }
          finishReject(ADMIN_CONSENT_FAILED_MESSAGE);
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, onMessage);
        dialog.addEventHandler(Office.EventType.DialogEventReceived, onDialogEvent);
      }
    );
  });
};

/** Prepare site grants or a Cloud Shell folder lookup without broad backend permissions. */
export const prepareSharedFolder = async (
  folderUrl: string,
  accessToken: string,
  targetType: SharedFolderTargetType = "folder"
): Promise<PrepareSharedFolderResponse> => {
  return requestStorage<PrepareSharedFolderResponse>(
    `${apiUrl}/admin/shared-folders/prepare`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ folder_url: folderUrl, target_type: targetType }),
    },
    true
  );
};
export const verifySharedFolder = async (
  registrationId: string,
  accessToken: string
): Promise<VerifySharedFolderResponse> => {
  return requestStorage<VerifySharedFolderResponse>(
    `${apiUrl}/admin/shared-folders/${encodeURIComponent(registrationId)}/verify`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
};
